import type {
  WShopSearchEntityView,
  WProductView,
  WCollectionItemProductView,
} from '@zola/svc-web-api-ts-client';

import { QUERY_MAP_TO_STATE } from '~/pages/ShopPages/constants';

import { convertCurrencyFormat } from '../../util/currencyFormatter';

const formatPriceForSegment = (centsPrice: number | undefined) => {
  if (!centsPrice) return null;
  // hide decimals if cents are zero
  const minPriceFractionDigits = (centsPrice / 100) % 1 > 0 ? 2 : 0;
  const formattedPrice = convertCurrencyFormat(
    centsPrice / 100,
    'USD',
    minPriceFractionDigits,
    2
  ) as string;
  return Number(formattedPrice.slice(1));
};

const getProductListViewedEventData = (
  products: WShopSearchEntityView[],
  trackedProductIndex: number,
  props: { eventData?: { location: string; section: string } }
) => {
  if (products.length === 0) return null;
  const { eventData } = props;
  if (!eventData) return null;
  const { location, section } = eventData; // undefined on brand page
  const productsData = [];

  for (let i = trackedProductIndex; i < products.length; i += 1) {
    const { product, kit } = products[i];
    const collectionItem = products[i].collection_item;

    if (product || collectionItem) {
      const item = (product || collectionItem) as WProductView | WCollectionItemProductView;
      const productLook = item.product_look_views?.[0];
      const sku = productLook?.sku_previews?.[0];
      const data = {
        product_id: item.id,
        sku: sku?.id,
        category: item.category_view && item.category_view.name,
        name: item.name,
        brand: item.brand && item.brand.name,
        variant: item.default_product_look_id,
        price: formatPriceForSegment(sku?.price_cents),
        quantity: item.suggested_quantity,
        position: i,
        url: `${window.location.origin}/shop/product/${productLook?.key}`,
      };
      productsData.push(data);
    } else if (kit) {
      // since kits lack some data that products have those missing properties will have null values
      const data = {
        product_id: kit.id,
        sku: null,
        category: 'KITS',
        name: kit.name,
        brand: kit.brand && kit.brand.name,
        variant: null,
        price: null,
        quantity: null,
        position: i,
        url: `${window.location.origin}/shop/kit/${kit.key}`,
      };
      productsData.push(data);
    }
  }
  return {
    products: productsData,
    location,
    section,
    endpoint_variant: null,
    recommendation_source: null,
  };
};

/**
 * Returns single selected color filter to be prepended to the meta title of a Category PLP.
 * Mostly for Baby pre-auth Category PLPs. For all other Category PLPs, it returns an empty string.
 */
const getMetaTitleSelectedFilter = ({
  isBaby,
  isGuest,
  query,
}: {
  isBaby?: boolean;
  isGuest: boolean;
  query?: string;
}): string => {
  if (!isBaby || !isGuest || !query) return '';

  const params = new URLSearchParams(query);
  const colorParam = params.get('color');
  if (params.size > 1 || !colorParam) return '';

  const [color, ...additionalColors] = colorParam.split(',');
  if (additionalColors.length > 0) return '';

  return color.toUpperCase() === 'MULTI'
    ? 'Multicolored'
    : color.charAt(0).toUpperCase() + color.slice(1).toLowerCase();
};

/**
 * Returns the meta title for a Category PLP.
 * Mostly for Baby pre-auth Category PLPs, for which we prepend a single color
 * filter and append "Registry" to the title, e.g. "Blue Diapering Registry".
 * For all other Category PLPs, we return the category name as is.
 */
export const getMetaTitle = ({
  categoryName,
  isBaby,
  isGuest,
  query,
}: {
  categoryName?: string;
  isBaby?: boolean;
  isGuest: boolean;
  query?: string;
}): string => {
  if (!categoryName) return '';
  if (!isBaby || !isGuest) return categoryName;
  const selectedFilter = getMetaTitleSelectedFilter({ isBaby, isGuest, query });
  const prefix = selectedFilter ? `${selectedFilter} ` : '';
  return `${prefix}${categoryName} Registry`;
};

/**
 * Determines whether a Category PLP is indexable.
 * Mostly for Baby pre-auth Category PLPs, which aren't indexed if price or less than
 * 20 multiple filters are applied. All other Category PLPs are indexable by default.
 */
export const getMetaNoindex = ({
  isBaby,
  isGuest,
  query,
}: {
  isBaby?: boolean;
  isGuest: boolean;
  query: string;
}): boolean => {
  if (!isBaby || !isGuest) return false;

  const validFilters = new Set(Object.keys(QUERY_MAP_TO_STATE));
  const params = new URLSearchParams(query).entries();
  let totalFilters = 0;
  let hasPrice = false;

  for (const [key, value] of params) {
    if (!validFilters.has(key)) continue;
    if (key === 'price') hasPrice = true;
    const count = value.split(',').length;
    totalFilters += count;
  }

  // Don't index Baby pre-auth Category PLPs with price filter
  if (hasPrice) return true;
  // Don't index Baby pre-auth Category PLPs with less than 20 multiple filters
  return totalFilters > 1 && totalFilters < 20;
};

export default getProductListViewedEventData;
